import type { AppDispatch } from 'store';

import { publishSessionMessage } from '~libs/sessionSync';
import { rejectReauthWaiters, resolveReauthWaiters } from '~libs/sessionReauth';

import { fetchSessionStatus } from '~main/actions/session';

import { removeAuthenticated, setReauthPrompt, SignOutReason } from '~main/reducers/login';

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;

/**
 * - `active`: the session is still usable (including when it now needs a password);
 * - `ended`: the API said there is no session, and the user has been signed out;
 * - `unavailable`: the API could not be asked (offline, 5xx, 503 `auth-unavailable`). Nothing was changed, and
 *   the caller decides when to ask again.
 */
export type SessionCheckResult = 'active' | 'ended' | 'unavailable';

// These take `dispatch` rather than importing the store, so components can use them through their own hook
// without pulling the store (and every reducer) into their module graph.

/**
 * Ends the session in this tab: state cleared, held requests released with a rejection, and other tabs told.
 * PrivateRoute redirects to /login, which explains why when the reason is `expired` or `unverified`.
 * Keys are left alone: only an explicit sign-out deletes them.
 */
export const expireSession = (dispatch: AppDispatch, reason?: SignOutReason) => {
  rejectReauthWaiters(reason ?? 'expired');
  dispatch(removeAuthenticated(reason));
  publishSessionMessage({ type: 'signed-out', reason: reason ?? 'expired' });
};

/**
 * The countdown reached zero. Ask the API before believing it, because another tab may have kept the session
 * alive. Only a 401 or 403 ends the session: a blip must not sign every tab out.
 */
export const resolveExpiredSession = async (dispatch: AppDispatch): Promise<SessionCheckResult> => {
  const result = await dispatch(fetchSessionStatus());

  if (fetchSessionStatus.fulfilled.match(result)) {
    if (result.payload.reauth_required) {
      // The reducer has already raised the prompt. Tell the other tabs so they show it too.
      publishSessionMessage({ type: 'reauth-required' });
    } else {
      // The prompt has left `required` without a reauth here (another tab confirmed): free anything held.
      resolveReauthWaiters();
    }
    return 'active';
  }

  const status = result.payload?.status;
  if (status !== HTTP_UNAUTHORIZED && status !== HTTP_FORBIDDEN) return 'unavailable';

  expireSession(dispatch, 'expired');
  return 'ended';
};

/** Raises the password prompt in this tab and the others. */
export const requirePasswordConfirmation = (dispatch: AppDispatch) => {
  dispatch(setReauthPrompt('required'));
  publishSessionMessage({ type: 'reauth-required' });
};
